import {
  Alert, Anchor, Box, Button, Divider, Group, Paper, Stack, Text, Title,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowRight, IconKey, IconSparkles, IconShieldLock, IconUsers } from '@tabler/icons-react';
import { Feature } from '../components/Feature';
import { tokens } from '../theme';
import { CONTACT_URL, PRIVACY_POLICY_URL } from '../lib/externalLinks';
import { CookiePreferencesButton } from '../components/Footer';

export type LandingViewProps = {
  signupBusy: boolean;
  signupError: string | null;
  onSignIn: () => void;
  onSignUp: () => void;
  // Visitor came from the Guardian trial form: lead with the trial, not a generic login.
  guardianTrial?: boolean;
};

export function LandingView(props: Readonly<LandingViewProps>) {
  const year = new Date().getFullYear();
  const trial = props.guardianTrial === true;

  return (
    <Box style={{
      minHeight: '100vh', background: tokens.navy,
      backgroundImage:
        `radial-gradient(circle at 12% 18%, rgba(59, 212, 129, 0.12), transparent 50%),` +
        `radial-gradient(circle at 88% 78%, rgba(0, 181, 248, 0.08), transparent 50%),` +
        `radial-gradient(circle at 50% 100%, rgba(13, 40, 57, 0.6), transparent 55%)`,
      display: 'flex', flexDirection: 'column',
    }}>
      <Box style={{ padding: '24px 32px' }}>
        <Group gap={12} align="center">
          <img
            src="/cloudhouse-logo.png"
            alt="Cloudhouse"
            style={{ height: 30, width: 'auto', objectFit: 'contain' }}
          />
        </Group>
      </Box>

      <Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 32px 48px' }}>
        <Box style={{ width: '100%', maxWidth: 1180, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 56, alignItems: 'center' }}>
          <Stack gap={36} style={{ maxWidth: 580 }}>
            <Stack gap={20}>
              <Box style={{
                alignSelf: 'flex-start', padding: '5px 14px', borderRadius: 999,
                border: `1px solid rgba(59, 212, 129, 0.3)`, background: tokens.greenSoft,
                color: tokens.green, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
              }}>
                {trial ? 'Cloudhouse Guardian' : 'The Cloudhouse Suite'}
              </Box>
              <Title order={1} style={{ color: tokens.white, fontSize: 48, lineHeight: 1.08, fontWeight: 700, letterSpacing: -0.8 }}>
                {trial ? (
                  <>
                    Start your{' '}
                    <Box component="span" style={{ color: tokens.green }}>
                      Guardian trial
                    </Box>
                  </>
                ) : (
                  <>
                    Your enterprise tools,{' '}
                    <Box component="span" style={{ color: tokens.green }}>
                      together
                    </Box>
                  </>
                )}
              </Title>
              <Text size="lg" style={{ color: tokens.textMuted, lineHeight: 1.55, maxWidth: 520 }}>
                {trial
                  ? 'Create your Cloudhouse account to start your free Guardian trial. It only takes a minute, and the same account works across every Cloudhouse tool.'
                  : 'One sign-in. Every Cloudhouse tool. Reach your full Cloudhouse SaaS suite — Foundry, Guardian, and more — from a single hub.'}
              </Text>
            </Stack>

            <Stack gap={20}>
              <Feature icon={<IconKey size={18} stroke={1.8} />} title="Single sign-on across products" description="Sign in once at Cloudhouse and stay signed in everywhere — Foundry Flow, Guardian, and beyond." />
              <Feature icon={<IconSparkles size={18} stroke={1.8} />} title="Discover new Cloudhouse tools" description="Every Cloudhouse SaaS appears as a tile — entitled, available, or coming soon." />
              <Feature icon={<IconUsers size={18} stroke={1.8} />} title="Manage access in one place" description="Centralised user, role, and product entitlement management for your organisation." />
            </Stack>
          </Stack>

          <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <Paper style={{
              width: '100%', maxWidth: 460,
              background: tokens.navyDeep,
              border: `1px solid ${tokens.navyBorder}`,
              borderRadius: 18, padding: 40,
              boxShadow: '0 24px 56px rgba(0, 0, 0, 0.5)',
            }}>
              <Stack gap={26}>
                <Stack gap={6}>
                  <Title order={3} style={{ color: tokens.white, fontSize: 22, letterSpacing: -0.3 }}>
                    {trial ? 'Create your account' : 'Welcome back'}
                  </Title>
                  <Text size="sm" c={tokens.textMuted} style={{ lineHeight: 1.5 }}>
                    {trial
                      ? 'Sign up with Cloudhouse to start your Guardian trial.'
                      : 'Sign in to your Cloudhouse account. Your existing Foundry Flow or Guardian login works here.'}
                  </Text>
                </Stack>
                {trial ? (
                  <>
                    <Button
                      size="md" radius={10} fullWidth
                      disabled={props.signupBusy}
                      rightSection={<IconArrowRight size={16} stroke={2.2} />}
                      styles={{ root: { background: tokens.green, color: tokens.navyDeeper, border: 'none', height: 46, fontWeight: 700, letterSpacing: 0.2 } }}
                      onClick={props.onSignUp}
                    >
                      Start your Guardian trial
                    </Button>
                    <Text size="sm" c={tokens.textMuted} ta="center">
                      Already have an account?{' '}
                      <Anchor component="button" type="button" size="sm" c={tokens.green} onClick={props.onSignIn}>
                        Sign in
                      </Anchor>
                    </Text>
                  </>
                ) : (
                  <>
                    <Button
                      size="md" radius={10} fullWidth
                      rightSection={<IconArrowRight size={16} stroke={2.2} />}
                      styles={{ root: { background: tokens.green, color: tokens.navyDeeper, border: 'none', height: 46, fontWeight: 700, letterSpacing: 0.2 } }}
                      onClick={props.onSignIn}
                    >
                      Sign in
                    </Button>
                    <Button
                      size="md" radius={10} fullWidth variant="default"
                      disabled={props.signupBusy}
                      onClick={props.onSignUp}
                      styles={{ root: { height: 46, fontWeight: 700, letterSpacing: 0.2 } }}
                    >
                      Sign up
                    </Button>
                  </>
                )}
                {props.signupError && (
                  <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light"
                         styles={{ root: { background: 'rgba(255,0,0,0.06)' } }}>
                    {props.signupError}
                  </Alert>
                )}
                <Divider label={<Text size="xs" c={tokens.textFaint}>or</Text>} labelPosition="center" styles={{ root: { borderTopColor: tokens.navyBorder } }} />
                <Group gap={10} align="flex-start" wrap="nowrap">
                  <IconShieldLock size={16} stroke={1.8} color={tokens.textMuted} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Text size="xs" c={tokens.textMuted} style={{ lineHeight: 1.6 }}>
                    Authentication is handled securely by WorkOS. Enterprise SSO comes with any paid subscription.
                  </Text>
                </Group>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Box style={{ padding: '20px 32px', borderTop: `1px solid ${tokens.navyBorder}`, background: tokens.navyDeep }}>
        <Group justify="space-between" align="center" wrap="wrap" gap={12} style={{ maxWidth: 1180, margin: '0 auto' }}>
          <Group gap={14} align="center" wrap="nowrap">
            <Box
              aria-hidden
              style={{ width: 8, height: 8, borderRadius: '50%', background: tokens.green, boxShadow: `0 0 10px ${tokens.green}` }}
            />
            <Text size="xs" c={tokens.textMuted} fw={600}>All systems operational</Text>
            <Box aria-hidden style={{ width: 1, height: 14, background: tokens.navyBorder }} />
            <Text size="xs" c={tokens.textFaint}>© {year} Cloudhouse · Enterprise Hub</Text>
          </Group>
          <Group gap={20}>
            <Anchor href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" size="xs" c={tokens.textMuted}>Privacy</Anchor>
            <CookiePreferencesButton color={tokens.textMuted} />
            <Anchor href={CONTACT_URL} target="_blank" rel="noreferrer" size="xs" c={tokens.textMuted}>Support</Anchor>
          </Group>
        </Group>
      </Box>
    </Box>
  );
}
